// Drain the outbox when online: upload photos, mutate entities, handle conflicts.
import axios from "axios";
import _ from "lodash";
import {
  getOutbox,
  setOutbox,
  removeOutboxItem,
  updateOutboxItem,
  addConflict,
  removeConflict,
  remapTempIdsInOutbox,
  offlineStatusSnapshot,
} from "./outbox";
import { rebuildRequestBody } from "./bodySerialize";
import { readPhotoAsFile, deletePhotoLocally } from "./photos";
import { writeThroughResponse, cacheSetEntity, cacheSetCurrentUser, guessSliceFromUrl, cacheRemoveEntity } from "./cache";
import { presentConflict } from "./conflict";
import { notifySyncSuccess, notifySyncClash } from "./notifications";
import { isOnline, isNetworkError } from "./network";
import { patchOfflineStatus } from "./status";
import { GET_CURRENT_USER } from "modules/current_user/actions";

// 5s, 15s, 45s, 2m15s, then cap at 5m
export const retryDelayMs = (attempts = 0) => Math.min(5 * 60 * 1000, 5000 * 3 ** Math.max(0, attempts));

const isRetryableSyncError = (error) => isNetworkError(error) || (error?.response?.status || 0) >= 500;

let running = false;
let storeRef = null;

export const setSyncStore = (store) => {
  storeRef = store;
};

const refreshStatus = async (extra = {}) => {
  if (!storeRef) return;
  storeRef.dispatch(
    patchOfflineStatus({
      ...(await offlineStatusSnapshot()),
      online: isOnline(),
      ...extra,
    })
  );
};

// UUID temp ids from offline creates (v4)
const isTempId = (id) => _.isString(id) && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

// Outbox drained but Redux/IDB still had _pendingSync — clear orphans so lists don't lie.
export const reconcileOrphanPendingFlags = async () => {
  if (!storeRef) return;
  const queue = await getOutbox();
  const stillPending = new Set();
  _.each(queue, (item) => {
    if (!item || (item.status !== "pending" && item.status !== "failed" && item.status !== "conflict")) return;
    if (item.entitySlice && item.entityId != null) {
      stillPending.add(`${item.entitySlice}:${item.entityId}`);
      stillPending.add(`${item.entitySlice}:${String(item.entityId)}`);
    }
    if (item.entitySlice && item.clientTempId) {
      stillPending.add(`${item.entitySlice}:${item.clientTempId}`);
    }
  });

  const state = storeRef.getState();
  _.each(state, (sliceState, slice) => {
    if (slice === "offline_status" || slice === "current_user") return;
    if (!_.isPlainObject(sliceState) || _.isArray(sliceState)) return;
    _.each(sliceState, (row) => {
      if (!row?._pendingSync || row.id == null) return;
      if (stillPending.has(`${slice}:${row.id}`) || stillPending.has(`${slice}:${String(row.id)}`)) return;

      if (isTempId(row.id)) {
        storeRef.dispatch({ type: "offline_entity_removed", payload: { slice, id: row.id } });
        cacheRemoveEntity(slice, row.id);
      } else {
        const cleaned = { ...row, _pendingSync: false };
        storeRef.dispatch({ type: "offline_entity_replaced", payload: { slice, data: cleaned } });
        cacheSetEntity(slice, cleaned);
      }
    });
  });
};

const clearPhotoEntries = async (serialized) => {
  if (serialized?.bodyKind !== "formData") return;
  await Promise.all(
    _.map(
      _.filter(serialized.entries, { kind: "file" }),
      (entry) => deletePhotoLocally(entry.localPath)
    )
  );
};

const fetchServerRecord = async (item) => {
  // Derive GET url for singular resource
  const url = item.url;
  if (!url || _.toLower(item.method) === "post") return null;
  try {
    const res = await axios.get(url, { skipOffline: true, skipOfflineWrite: true, timeout: 15000 });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
};

const applyKeepServer = async (item, serverRecord) => {
  await removeOutboxItem(item.id);
  await removeConflict(item.id);
  const slice = item.entitySlice || guessSliceFromUrl(item.url);
  if (slice && serverRecord) {
    await cacheSetEntity(slice, serverRecord);
    if (storeRef && serverRecord.id != null) {
      // Best-effort Redux update via generic fulfilled shape for list merge
      storeRef.dispatch({
        type: "offline_entity_replaced",
        payload: { slice, data: serverRecord },
      });
    }
  }
  await clearPhotoEntries(item);
};

const runOne = async (rawItem) => {
  let item = rawItem;
  if (item.status === "conflict") return { conflict: true };

  const method = _.toLower(item.method);

  if (method === "patch" || method === "put") {
    let serverRecord = null;
    try {
      serverRecord = await fetchServerRecord(item);
    } catch (_e) {
      // continue — may still patch
    }
    if (
      !item.force &&
      serverRecord &&
      item.baseUpdatedAt &&
      serverRecord.updated_at &&
      serverRecord.updated_at !== item.baseUpdatedAt
    ) {
      await updateOutboxItem(item.id, { status: "conflict" });
      await addConflict({
        outboxId: item.id,
        label: serverRecord.display_name || `#${serverRecord.id}`,
        serverRecord,
        outboxItem: item,
      });
      await notifySyncClash();
      const choice = await presentConflict({
        label: serverRecord.display_name || `Record #${serverRecord.id}`,
      });
      if (choice === "server") {
        await applyKeepServer(item, serverRecord);
        return { skipped: true };
      }
      // keep phone — fall through with force so we don't re-prompt
      await updateOutboxItem(item.id, { status: "pending", force: true });
      await removeConflict(item.id);
      item = { ...item, force: true, status: "pending" };
    }
  }

  // Upload offline photos first → blob_signed_id, then rebuild entity body without raw files
  let serialized = item;
  if (item.bodyKind === "formData") {
    const entries = [];
    for (const entry of item.entries || []) {
      if (entry.kind === "file" && (entry.attachmentUpload || _.includes(entry.key, "attachment"))) {
        const file = await readPhotoAsFile(entry);
        const fd = new FormData();
        fd.append("attachment", file, entry.name || file.name);
        const uploadRes = await axios.post("/attachments/create", fd, { skipOffline: true, timeout: 120000 });
        const signedId = uploadRes.data?.blob_signed_id;
        if (entry.signedIdKey && signedId) {
          entries.push({ key: entry.signedIdKey, kind: "string", value: signedId });
        } else if (signedId) {
          const prefix = entry.key.replace(/\[attachment\]$/, "").replace(/\[__offline_file\]$/, "");
          entries.push({ key: `${prefix}[blob_signed_id]`, kind: "string", value: signedId });
        }
        // drop the file entry from the entity payload
        continue;
      }
      if (entry.kind === "string" && (_.endsWith(entry.key, "[localPath]") || _.endsWith(entry.key, "[pendingUpload]"))) {
        continue;
      }
      entries.push(entry);
    }
    serialized = { ...item, entries };
  }

  const body = await rebuildRequestBody(serialized, { readPhotoAsFile });
  const res = await axios({
    method: item.method,
    url: item.url,
    data: body,
    headers: item.headers || {},
    skipOffline: true,
    timeout: item.bodyKind === "formData" ? 120000 : 20000,
  });

  const slice = item.entitySlice || guessSliceFromUrl(item.url);

  if (method === "delete") {
    if (slice && item.entityId != null) {
      await cacheRemoveEntity(slice, item.entityId);
      if (storeRef) {
        storeRef.dispatch({ type: "offline_entity_removed", payload: { slice, id: item.entityId } });
      }
    }
  } else if (res?.data) {
    await writeThroughResponse(method, item.url, res.data);
    if (slice === "current_user") {
      await cacheSetCurrentUser(res.data);
      if (storeRef) {
        storeRef.dispatch({ type: `${GET_CURRENT_USER}_FULFILLED`, payload: { data: res.data } });
      }
    } else if (item.clientTempId && res.data.id) {
      await remapTempIdsInOutbox(item.clientTempId, res.data.id);
      await cacheRemoveEntity(slice, item.clientTempId);
      if (slice && storeRef) {
        storeRef.dispatch({
          type: "offline_temp_id_remapped",
          payload: { slice, clientTempId: item.clientTempId, serverId: res.data.id, data: res.data },
        });
      }
    } else if (slice && storeRef && res.data.id != null) {
      // patch/put (or post without temp) — clear sticky _pendingSync in Redux
      storeRef.dispatch({
        type: "offline_entity_replaced",
        payload: { slice, data: { ...res.data, _pendingSync: false } },
      });
    }
  }

  await clearPhotoEntries(item);
  await removeOutboxItem(item.id);
  await removeConflict(item.id);
  return { synced: true };
};

const itemReady = (item, now) => {
  if (!item) return false;
  if (item.status === "conflict") return false;
  if (item.status !== "pending" && item.status !== "failed") return false;
  return !item.nextRetryAt || item.nextRetryAt <= now;
};

export const runSync = async () => {
  if (running) return { syncedCount: 0, busy: true };
  if (!isOnline()) return { syncedCount: 0, offline: true };
  running = true;
  await refreshStatus({ syncing: true, lastError: null });

  let syncedCount = 0;
  let hadConflict = false;
  const tried = new Set();

  try {
    // Drain FIFO — re-read queue each time so remaps apply
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const now = Date.now();
      const queue = await getOutbox();
      const next = _.find(queue, (i) => itemReady(i, now) && !tried.has(i.id));
      if (!next) break;
      if (!isOnline()) break;
      tried.add(next.id);

      try {
        const result = await runOne(next);
        if (result.synced) syncedCount += 1;
        if (result.conflict) {
          hadConflict = true;
          break;
        }
      } catch (e) {
        const attempts = (next.attempts || 0) + 1;
        const retryable = isRetryableSyncError(e);
        const lastError = _.isString(e?.response?.data?.error)
          ? e.response.data.error
          : e?.message || "sync failed";
        await updateOutboxItem(next.id, {
          status: "failed",
          lastError,
          attempts,
          nextRetryAt: retryable ? Date.now() + retryDelayMs(attempts) : null,
        });
        if (storeRef) {
          storeRef.dispatch(patchOfflineStatus({ lastError }));
        }
        // Retryable (dead tower, 5xx): keep draining the rest. Hard 4xx: skip this item.
        if (!retryable && !isOnline()) break;
      }
    }

    if (syncedCount > 0 && !hadConflict) {
      await notifySyncSuccess(syncedCount);
    }
  } finally {
    running = false;
    await reconcileOrphanPendingFlags();
    await refreshStatus({ syncing: false, ...(syncedCount > 0 ? { lastSyncedAt: Date.now() } : {}) });
  }

  return { syncedCount, hadConflict };
};

export const retryFailedNow = async () => {
  const queue = await getOutbox();
  const reset = _.map(queue, (item) =>
    item.status === "failed" ? { ...item, status: "pending", nextRetryAt: null } : item
  );
  await setOutbox(reset);
  return runSync();
};

export const resolveConflictChoice = async ({ choice, outboxItem, serverRecord }) => {
  if (choice === "server") {
    await applyKeepServer(outboxItem, serverRecord);
    await refreshStatus();
    return;
  }
  await updateOutboxItem(outboxItem.id, { status: "pending" });
  await removeConflict(outboxItem.id);
  await runSync();
};
